import { queryOptions, useQuery } from "@tanstack/react-query";
import { fetchLang, useFetchLang } from "../helpers/language.helpers";
import { apiGet } from "../helpers/request.helpers";
import { useAuth } from "../AuthContext";

// language is read per call, not at module load: a language switch does not
// reload the page, and invalidations elsewhere key on the current language.
export const userCoursesProgressQueryOptions = ({
  userId,
  enabled,
  language = fetchLang(),
}: {
  userId: number | null;
  enabled?: boolean;
  language?: string;
}) => {
  return queryOptions<
    {
      list: UserCourseProgressResponse[];
      map: Map<string, UserCourseProgressResponse>;
    },
    Error
  >({
    queryKey: ["user-courses-progress", userId, language],
    queryFn: async () => {
      const response = await apiGet("/statistics/list-user-courses-progress", {
        user_id: userId,
        language,
      });
      const list: UserCourseProgressResponse[] = response.CourseProgresses;
      const map: Map<string, UserCourseProgressResponse> = new Map();
      list.forEach((p) => {
        map.set(p.PublicCourseUUID, p);
      });
      return { list, map };
    },
    enabled: enabled && userId !== null,
  });
};

export type UserCourseProgressResponse = {
  PublicCourseUUID: string;
  Name: string;
  ImageUUID: string;
  IsLinear: boolean;
  Individual: boolean;
  CompletedCount: number;
  ExerciseCount: number;
  StylePointsAvailable: number;
  StylePointsEarned: number;
  Runtime: number;
  IsAssigned: boolean;
  Vehicles: string[];
};

export function useUserCoursesProgress(user_id: number | null) {
  const { isAuthenticated } = useAuth();
  const language = useFetchLang();
  return useQuery(
    userCoursesProgressQueryOptions({
      userId: user_id,
      enabled: isAuthenticated,
      language,
    })
  );
}
